import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { extractErrorMessage } from '@/api/extractErrorMessage';
import { gqlClient } from '@/api/graphql-client';
import proposalLineItemKeys from '@/api/queries/proposalLineItems/keyFactory';
import { graphql } from '@/gql';
import { GetProposalLineItemQuery } from '@/gql/graphql';

export const ProposalLineItemFragment = graphql(`
  fragment ProposalLineItem on ProposalLineItem {
    id
    publicId
    internalReference
    description
    ...LineItemEditorFields
    quantity
    thumbnail {
      id
      filename
      url
    }
    selection {
      type
      selected
    }
    lineItem {
      id
    }
  }
`);

const GET_PROPOSAL_LINE_ITEM = graphql(`
  query GetProposalLineItem($proposalLineItemId: ID!) {
    proposalLineItem(id: $proposalLineItemId) {
      ...ProposalLineItem
    }
  }
`);

export type ProposalLineItemData = GetProposalLineItemQuery['proposalLineItem'];

const useGetProposalLineItem = (
  opts: {
    proposalLineItemId?: string;
  } = {},
  queryOptions?: Omit<
    UseQueryOptions<GetProposalLineItemQuery>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery({
    ...queryOptions,
    enabled: !!opts.proposalLineItemId,
    queryKey: proposalLineItemKeys.proposalLineItem(opts.proposalLineItemId!),
    queryFn: () => {
      return gqlClient.request(GET_PROPOSAL_LINE_ITEM, {
        proposalLineItemId: opts.proposalLineItemId!,
      });
    },
    // A client can hold the id of a Line Item that has since been deleted, so
    // this query failing is a normal outcome rather than a fault. Retrying
    // cannot recover it, and the editors render a not-found state instead.
    retry: (failureCount, error) =>
      failureCount < 3 && extractErrorMessage(error).code !== 'NOT_FOUND',
  });
};

export default useGetProposalLineItem;
